/**
 * Payment API
 *
 * Frontend API functions for the Xendit-based payment endpoints.
 */

import { apiClient } from '@/shared/lib/api-client';
import type {
  BillingCycle,
  PlanTier,
  CreateSubscriptionResponse,
  CancelSubscriptionResponse,
  Invoice,
} from '../types/billing.types';

const BASE = '/payment';

/** Create a payment invoice; returns checkoutUrl for redirect-based checkout */
export function createSubscription(
  plan: Exclude<PlanTier, 'free'>,
  cycle: BillingCycle,
  currency?: string
): Promise<CreateSubscriptionResponse> {
  return apiClient.post<CreateSubscriptionResponse>(`${BASE}/subscribe`, {
    plan,
    cycle,
    currency,
  });
}

/** List recent invoices for the business */
export function getInvoices(limit = 10): Promise<Invoice[]> {
  return apiClient.get<Invoice[]>(`${BASE}/invoices?limit=${limit}`);
}

/** Cancel subscription at end of billing period */
export function cancelSubscription(): Promise<CancelSubscriptionResponse> {
  return apiClient.post<CancelSubscriptionResponse>(`${BASE}/cancel`, {});
}

/** Reactivate a subscription that was scheduled for cancellation */
export function reactivateSubscription(): Promise<
  import('../types/billing.types').UsageSummary
> {
  return apiClient.post<import('../types/billing.types').UsageSummary>(
    `${BASE}/reactivate`,
    {}
  );
}

/** Cancel a pending paid→paid downgrade */
export function cancelPendingDowngrade(): Promise<void> {
  return apiClient.post<void>(`${BASE}/cancel-pending-downgrade`, {});
}

// ── Manual payment flow ───────────────────────────────────────────────────

export interface PendingPaymentRequest {
  _id: string;
  plan: string;
  cycle: 'monthly' | 'annual';
  currency: string;
  requestType: 'subscribe' | 'upgrade' | 'addon';
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  requestedAt: string;
  message?: string;
  requestedLimits?: {
    storehouses?: number;
    users?: number;
    items?: number;
    monthlyTransactions?: number;
  };
}

/** Get the active pending payment request for this business (null if none) */
export function getPendingRequest(): Promise<{
  request: PendingPaymentRequest | null;
}> {
  return apiClient.get<{ request: PendingPaymentRequest | null }>(
    `${BASE}/pending-request`
  );
}

/** Cancel the active pending payment request */
export function cancelPaymentRequest(): Promise<{ cancelled: boolean }> {
  return apiClient.post<{ cancelled: boolean }>(`${BASE}/cancel-request`, {});
}

// ── Add-on request ──────────────────────────────────────────────

export interface RequestAddOnPayload {
  message?: string;
  requestedLimits?: {
    storehouses?: number;
    users?: number;
    items?: number;
    monthlyTransactions?: number;
  };
  cycle: 'monthly' | 'annual';
  currency?: string;
}

/** Submit an add-on request (any paid plan) */
export function requestAddOn(
  payload: RequestAddOnPayload
): Promise<{ requestId: string }> {
  return apiClient.post<{ requestId: string }>(
    `${BASE}/request-addon`,
    payload
  );
}
